// Adapted from: https://github.com/segmentio/evergreen/blob/26d59831d28f44dd772b78c95364ce5f46c453b8/src/positioner/src/Positioner.js
import { bool, func, number, oneOf, object } from 'prop-types';
import React, { useLayoutEffect, useRef, useState } from 'react';
import Portal from '../Portal';
import { getPosition, Position } from './util';

const initialState = {
  top: null,
  left: null,
  transformOrigin: null,
};

const Positioner = ({
  open = false,
  renderContent,
  children = undefined,
  position = Position.BOTTOM,
  targetOffset = 6,
  bodyOffset = 6,
  contentRef: contentRefProp = undefined,
  targetRef: targetRefProp = undefined,
  zIndex = 10,
}) => {
  const [state, setState] = useState(initialState);
  const defaultContentRef = useRef();
  const defaultTargetRef = useRef();

  const contentRef = contentRefProp || defaultContentRef;
  const targetRef = targetRefProp || defaultTargetRef;

  function update() {
    if (!open || !targetRef.current || !contentRef.current) return;

    const targetRect = targetRef.current.getBoundingClientRect();

    const viewportHeight = document.documentElement.clientHeight;
    const viewportWidth = document.documentElement.clientWidth;

    const contentRect = contentRef.current.getBoundingClientRect();

    // We need to round the width and height to prevent jitter when
    // the window is zoomed (when `window.devicePixelRatio` is not an integer)
    const height = Math.round(contentRect.height);
    const width = Math.round(contentRect.width);

    const { rect, transformOrigin } = getPosition({
      position,
      targetRect,
      targetOffset,
      dimensions: {
        height,
        width,
      },
      viewport: {
        width: viewportWidth,
        height: viewportHeight,
      },
      viewportOffset: bodyOffset,
    });

    setState({
      left: rect.left,
      top: rect.top,
      transformOrigin,
    });
  }

  const { top, left, transformOrigin } = state;

  useLayoutEffect(() => {
    const rafId = requestAnimationFrame(update);
    return () => {
      cancelAnimationFrame(rafId);
    };
  });

  return (
    <>
      {children?.({ ref: targetRef })}
      <Portal>
        {renderContent({
          top,
          left,
          zIndex,
          style: {
            position: 'fixed',
            transformOrigin,
            left,
            top,
            zIndex,
          },
          ref: contentRef,
        })}
      </Portal>
    </>
  );
};

Positioner.propTypes = {
  /**
   * The position the element that is being positioned is on.
   * Smart positioning might override this.
   */
  position: oneOf([
    Position.TOP,
    Position.TOP_LEFT,
    Position.TOP_RIGHT,
    Position.BOTTOM,
    Position.BOTTOM_LEFT,
    Position.BOTTOM_RIGHT,
    Position.LEFT,
    Position.RIGHT,
  ]),

  /**
   * When true, show the element being positioned.
   */
  open: bool,

  /**
   * The minimum distance from the body to the element being positioned.
   */
  bodyOffset: number,

  /**
   * The minimum distance from the target to the element being positioned.
   */
  targetOffset: number,

  /**
   * Function that should return a node for the target.
   * ({ ref }) -> React Node
   */
  children: func,

  /**
   * Function that returns the element being positioned.
   * ({ ref, style }) -> React Node
   */
  renderContent: func.isRequired,

  targetRef: object,

  contentRef: object,

  zIndex: number,
};

export default Positioner;
export { Position };
